/**
 * Environment Configuration Manager
 *
 * Provides type-safe access to environment variables with validation
 * Ensures all required configuration is present before application starts
 *
 * Security: Only public keys and non-sensitive configuration should be accessed here
 * All secret keys and sensitive data must remain on the backend
 */

interface EnvironmentConfig {
  // Application Environment
  APP_ENV: 'development' | 'staging' | 'production';
  API_BASE_URL: string;
  USE_REAL_API: boolean;

  // Data Storage
  FORCE_LOCALSTORAGE: boolean;
  HYBRID_MODE: boolean;
  ENCRYPTION_KEY?: string;

  // External Services (Public Keys Only)
  STRIPE_PUBLIC_KEY?: string;
  GOOGLE_ANALYTICS_ID?: string;
  GOOGLE_MAPS_API_KEY?: string;
  RECAPTCHA_SITE_KEY?: string;
  VAPID_PUBLIC_KEY?: string;

  // Feature Flags
  ENABLE_AI_FEATURES: boolean;
  ENABLE_ANALYTICS: boolean;
  ENABLE_DEBUG_MODE: boolean;
  ENABLE_PWA: boolean;

  // UI/UX Configuration
  DEFAULT_LANGUAGE: 'ja' | 'en';
  DEFAULT_THEME: 'light' | 'dark' | 'auto';
  DEFAULT_DATE_FORMAT: string;
  DEFAULT_TIME_FORMAT: '12h' | '24h';

  // Performance
  ENABLE_CACHE: boolean;
  CACHE_TTL: number;
  MAX_API_RETRIES: number;
  API_TIMEOUT: number;

  // Security
  CSP_ENABLED: boolean;
  FRAME_OPTIONS: string;
}

class EnvironmentManager {
  private config: EnvironmentConfig;
  private readonly isDevelopment: boolean;
  private readonly isProduction: boolean;
  private readonly isStaging: boolean;

  constructor() {
    this.config = this.loadConfig();
    this.isDevelopment = this.config.APP_ENV === 'development';
    this.isStaging = this.config.APP_ENV === 'staging';
    this.isProduction = this.config.APP_ENV === 'production';
    this.validateConfig();
  }

  private loadConfig(): EnvironmentConfig {
    // Use import.meta.env for Vite
    const env = import.meta.env || {};

    return {
      // Application Environment
      APP_ENV: (env.VITE_APP_ENV ||
        'development') as EnvironmentConfig['APP_ENV'],
      API_BASE_URL: this.getSecureApiBaseUrl(env),
      USE_REAL_API: this.parseBoolean(env.VITE_USE_REAL_API, false),

      // Data Storage
      FORCE_LOCALSTORAGE: this.parseBoolean(env.VITE_FORCE_LOCALSTORAGE, true), // Default to localStorage for MVP
      HYBRID_MODE: this.parseBoolean(env.VITE_HYBRID_MODE, false),
      ENCRYPTION_KEY: env.VITE_ENCRYPTION_KEY,

      // External Services
      STRIPE_PUBLIC_KEY: env.VITE_STRIPE_PUBLIC_KEY,
      GOOGLE_ANALYTICS_ID: env.VITE_GOOGLE_ANALYTICS_ID,
      GOOGLE_MAPS_API_KEY: env.VITE_GOOGLE_MAPS_API_KEY,
      RECAPTCHA_SITE_KEY: env.VITE_RECAPTCHA_SITE_KEY,
      VAPID_PUBLIC_KEY: env.VITE_VAPID_PUBLIC_KEY,

      // Feature Flags
      ENABLE_AI_FEATURES: this.parseBoolean(env.VITE_ENABLE_AI_FEATURES, false),
      ENABLE_ANALYTICS: this.parseBoolean(env.VITE_ENABLE_ANALYTICS, true),
      ENABLE_DEBUG_MODE: this.parseBoolean(env.VITE_ENABLE_DEBUG_MODE, false),
      ENABLE_PWA: this.parseBoolean(env.VITE_ENABLE_PWA, true),

      // UI/UX Configuration
      DEFAULT_LANGUAGE: (env.VITE_DEFAULT_LANGUAGE ||
        'ja') as EnvironmentConfig['DEFAULT_LANGUAGE'],
      DEFAULT_THEME: (env.VITE_DEFAULT_THEME ||
        'light') as EnvironmentConfig['DEFAULT_THEME'],
      DEFAULT_DATE_FORMAT: env.VITE_DEFAULT_DATE_FORMAT || 'YYYY/MM/DD',
      DEFAULT_TIME_FORMAT: (env.VITE_DEFAULT_TIME_FORMAT ||
        '24h') as EnvironmentConfig['DEFAULT_TIME_FORMAT'],

      // Performance
      ENABLE_CACHE: this.parseBoolean(env.VITE_ENABLE_CACHE, true),
      CACHE_TTL: this.parseNumber(env.VITE_CACHE_TTL, 3600),
      MAX_API_RETRIES: this.parseNumber(env.VITE_MAX_API_RETRIES, 3),
      API_TIMEOUT: this.parseNumber(env.VITE_API_TIMEOUT, 30000),

      // Security
      CSP_ENABLED: this.parseBoolean(env.VITE_CSP_ENABLED, false),
      FRAME_OPTIONS: env.VITE_FRAME_OPTIONS || 'SAMEORIGIN',
    };
  }

  private parseBoolean(
    value: string | undefined,
    defaultValue: boolean
  ): boolean {
    if (value === undefined || value === '') return defaultValue;
    return value === 'true' || value === '1';
  }

  private parseNumber(value: string | undefined, defaultValue: number): number {
    if (value === undefined || value === '') return defaultValue;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? defaultValue : parsed;
  }

  /**
   * Get API Base URL with intelligent environment-based routing
   *
   * Priority Order:
   * 1. Explicit VITE_API_BASE_URL environment variable
   *    - Production/Staging: Manually configured in Vercel
   *    - Preview: Auto-injected by GitHub Action (.github/workflows/preview-deploy-sequencer.yml)
   *      → Backend deploys first, GitHub Action sets VITE_API_BASE_URL, then Frontend deploys
   * 2. Custom domain routing based on environment:
   *    - Production (main): https://api.ripia.jp
   *    - Staging (develop): https://api-staging.ripia.jp
   * 3. Preview (all PRs): Dynamic URL inference (fallback only, not recommended)
   *    - Uses VERCEL_BRANCH_URL for stable Git-based URLs
   *    - Falls back to VERCEL_URL for commit-specific URLs
   * 4. Local development: http://localhost:3000
   *
   * Preview URL Strategy (Sequential Deployment):
   * GitHub Action ensures Backend URL is always correct for Preview environments.
   * No dynamic inference needed - reliable URL pairing guaranteed.
   *
   * Security: Only allows localhost fallback in development mode
   */
  private getSecureApiBaseUrl(env: Record<string, string | undefined>): string {
    const apiBaseUrl = env.VITE_API_BASE_URL?.trim();
    const appEnv = env.VITE_APP_ENV || env.NODE_ENV || 'development';
    // Use VITE_ prefixed versions for client-side access
    const vercelEnv = env.VITE_VERCEL_ENV; // Vercel environment: production | preview | development
    const vercelBranch = env.VITE_VERCEL_GIT_COMMIT_REF; // Branch name (e.g., "main", "develop", "feature/xyz")
    const vercelBranchUrl = env.VITE_VERCEL_BRANCH_URL; // Git-based stable URL (e.g., "ripia-git-feature-xxx.vercel.app")
    const vercelUrl = env.VITE_VERCEL_URL; // Vercel deployment URL (commit-based)

    // Debug logging to help identify environment detection issues
    console.log('🔍 Environment Detection Debug (Sequential Deployment):', {
      VITE_API_BASE_URL: apiBaseUrl,
      VITE_APP_ENV: env.VITE_APP_ENV,
      VITE_VERCEL_ENV: vercelEnv,
      VITE_VERCEL_GIT_COMMIT_REF: vercelBranch,
      VITE_VERCEL_BRANCH_URL: vercelBranchUrl,
      VITE_VERCEL_URL: vercelUrl,
      NODE_ENV: env.NODE_ENV,
    });

    // PRIORITY 1: Explicit environment variable override
    if (apiBaseUrl) {
      console.log(`✅ Using explicit VITE_API_BASE_URL: ${apiBaseUrl}`);
      return apiBaseUrl;
    }

    // PRIORITY 2: Production environment (main branch)
    if (vercelEnv === 'production' || vercelBranch === 'main') {
      const productionUrl = 'https://api.ripia.jp';
      console.log(
        `✅ Production environment detected, using: ${productionUrl}`
      );
      return productionUrl;
    }

    // PRIORITY 3: Staging environment (develop branch)
    if (vercelBranch === 'develop' || appEnv === 'staging') {
      const stagingUrl = 'https://api-staging.ripia.jp';
      console.log(`✅ Staging environment detected, using: ${stagingUrl}`);
      return stagingUrl;
    }

    // PRIORITY 4: Preview/PR environment (dynamic Backend URL inference)
    // Strategy: Infer Backend URL from Frontend URL by replacing project name
    // Frontend: ripia-git-[branch].vercel.app → Backend: ripia-backend-git-[branch].vercel.app
    // Frontend: ripia-[hash].vercel.app → Backend: ripia-backend-[hash].vercel.app
    if (vercelEnv === 'preview') {
      // Option A: Explicit environment variable (highest priority for Preview)
      if (apiBaseUrl) {
        console.log(
          `✅ Preview environment using explicit VITE_API_BASE_URL: ${apiBaseUrl}`
        );
        return apiBaseUrl;
      }

      // Option B: Git-based branch URL (stable, recommended)
      // VERCEL_BRANCH_URL provides stable URLs like "ripia-git-feature-xyz.vercel.app"
      if (vercelBranchUrl) {
        // Transform: ripia-git-[branch] → ripia-backend-git-[branch]
        const backendUrl = `https://${vercelBranchUrl.replace(/^ripia-/, 'ripia-backend-')}`;
        console.log(`✅ Preview environment (git-based URL)`);
        console.log(`   Frontend Branch URL: ${vercelBranchUrl}`);
        console.log(`   Backend URL (inferred): ${backendUrl}`);
        console.log(`   Note: Stable URL tied to Git branch`);
        return backendUrl;
      }

      // Option C: Deployment URL fallback (commit-specific)
      // VERCEL_URL provides commit-specific URLs like "ripia-abc123xyz.vercel.app"
      if (vercelUrl) {
        // Transform: ripia-[hash] → ripia-backend-[hash]
        const backendUrl = `https://${vercelUrl.replace(/^ripia-/, 'ripia-backend-')}`;
        console.warn(
          `⚠️ Preview environment using deployment URL (commit-specific)`
        );
        console.log(`   Frontend Deployment URL: ${vercelUrl}`);
        console.log(`   Backend URL (inferred): ${backendUrl}`);
        console.log(`   Note: URL changes with each deployment`);
        return backendUrl;
      }

      // No valid URL found for Preview environment
      const errorMsg = `❌ Preview environment Backend URL could not be determined:
   VITE_VERCEL_BRANCH_URL: ${vercelBranchUrl || 'not set'}
   VITE_VERCEL_URL: ${vercelUrl || 'not set'}

   Please ensure Vercel system environment variables are properly exposed to client:
   - VERCEL_BRANCH_URL → VITE_VERCEL_BRANCH_URL
   - VERCEL_URL → VITE_VERCEL_URL`;

      console.error(errorMsg);
      throw new Error(errorMsg);
    }

    // PRIORITY 5: Local development and test
    if (appEnv === 'development' || appEnv === 'test') {
      console.warn(
        `⚠️ Using localhost fallback for API_BASE_URL in ${appEnv} environment`
      );
      return 'http://localhost:3000';
    }

    // PRIORITY 6: Fail-safe fallback - should never reach here
    const errorMessage =
      `❌ Unable to determine API_BASE_URL:
` +
      `   VERCEL_ENV=${vercelEnv}
` +
      `   VERCEL_GIT_COMMIT_REF=${vercelBranch}
` +
      `   APP_ENV=${appEnv}
` +
      `   Please set VITE_API_BASE_URL in Vercel environment variables or check your deployment configuration.`;

    console.error(errorMessage);
    throw new Error(errorMessage);
  }

  private validateConfig(): void {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required fields for production
    if (this.isProduction) {
      // Critical security validation (hard errors)
      if (!import.meta.env?.VITE_API_BASE_URL) {
        errors.push(
          'VITE_API_BASE_URL is required in production - localhost fallback not allowed'
        );
      }

      // Security warnings (recommended but not blocking)
      if (!this.config.ENCRYPTION_KEY) {
        warnings.push(
          'ENCRYPTION_KEY not configured - data encryption disabled (recommended for production)'
        );
      }

      // Optional services (warnings only)
      if (!this.config.STRIPE_PUBLIC_KEY) {
        warnings.push(
          'STRIPE_PUBLIC_KEY not configured - payment features will be disabled'
        );
      }
      if (!this.config.GOOGLE_ANALYTICS_ID) {
        warnings.push(
          'GOOGLE_ANALYTICS_ID not configured - analytics tracking will be disabled'
        );
      }
    }

    // Staging environment validation
    if (this.isStaging) {
      // Note: import.meta.env may not be fully populated during build time
      // Actual API_BASE_URL validation happens in getSecureApiBaseUrl()
      if (
        !this.config.API_BASE_URL ||
        this.config.API_BASE_URL === 'http://localhost:3000'
      ) {
        warnings.push(
          'VITE_API_BASE_URL not explicitly set in staging - using auto-detection'
        );
      }
    }

    // Display warnings (non-blocking)
    if (warnings.length > 0) {
      console.warn(
        '⚠️ Environment Configuration Warnings:\n' + warnings.join('\n')
      );
    }

    // Log warnings in development, throw errors in production
    if (errors.length > 0) {
      const message = `Environment configuration errors:\n${errors.join('\n')}`;

      if (this.isProduction) {
        throw new Error(message);
      } else {
        console.warn('⚠️ Environment Configuration Warning:', message);
      }
    }

    // Security warnings
    if (this.config.ENABLE_DEBUG_MODE && this.isProduction) {
      console.warn('⚠️ Security Warning: Debug mode is enabled in production!');
    }

    // Log configuration summary (excluding sensitive data)
    if (this.config.ENABLE_DEBUG_MODE) {
      console.log('🔧 Environment Configuration:', {
        environment: this.config.APP_ENV,
        apiBaseUrl: this.config.API_BASE_URL,
        useRealApi: this.config.USE_REAL_API,
        forceLocalStorage: this.config.FORCE_LOCALSTORAGE,
        features: {
          ai: this.config.ENABLE_AI_FEATURES,
          analytics: this.config.ENABLE_ANALYTICS,
          pwa: this.config.ENABLE_PWA,
        },
      });
    }
  }

  // Getter methods for accessing configuration
  get<K extends keyof EnvironmentConfig>(key: K): EnvironmentConfig[K] {
    return this.config[key];
  }

  // Convenience methods
  isDev(): boolean {
    return this.isDevelopment;
  }

  isProd(): boolean {
    return this.isProduction;
  }

  isStage(): boolean {
    return this.isStaging;
  }

  isDebugMode(): boolean {
    return this.config.ENABLE_DEBUG_MODE;
  }

  // Feature flag checks
  isFeatureEnabled(feature: 'ai' | 'analytics' | 'pwa' | 'cache'): boolean {
    switch (feature) {
      case 'ai':
        return this.config.ENABLE_AI_FEATURES;
      case 'analytics':
        return this.config.ENABLE_ANALYTICS;
      case 'pwa':
        return this.config.ENABLE_PWA;
      case 'cache':
        return this.config.ENABLE_CACHE;
      default:
        return false;
    }
  }

  // API configuration
  getApiConfig() {
    return {
      baseUrl: this.config.API_BASE_URL,
      timeout: this.config.API_TIMEOUT,
      retries: this.config.MAX_API_RETRIES,
      useRealApi: this.config.USE_REAL_API,
    };
  }

  // Stripe configuration (public only)
  getStripeConfig() {
    return {
      publicKey: this.config.STRIPE_PUBLIC_KEY,
      // Note: Secret keys are never exposed to frontend
    };
  }

  // Storage configuration
  getStorageConfig() {
    return {
      forceLocalStorage: this.config.FORCE_LOCALSTORAGE,
      hybridMode: this.config.HYBRID_MODE,
      encryptionKey: this.config.ENCRYPTION_KEY,
    };
  }

  // UI configuration
  getUIConfig() {
    return {
      language: this.config.DEFAULT_LANGUAGE,
      theme: this.config.DEFAULT_THEME,
      dateFormat: this.config.DEFAULT_DATE_FORMAT,
      timeFormat: this.config.DEFAULT_TIME_FORMAT,
    };
  }
}

// Create singleton instance
const env = new EnvironmentManager();

// Export both the instance and the class for testing
export { EnvironmentManager };
export default env;
